import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { cn } from "@/shared/lib/utils";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { InfiniteScrollContainer } from "@/shared/components/InfiniteScrollContainer";
import { EmptyState } from "@/shared/components/EmptyState";
import { Button } from "@/shared/components/button";

// A fixed 5-row height: each row is ~62px (12px padding top+bottom, a 36px
// two-line text block, 2px border) with 12px gaps, so 5 × 62 + 4 × 12 = 358px.
// Fixed (not max-h) so the Upload Records page's two side-by-side cards are
// always the same height, whether a card is empty, short, or full.
const SCROLL_HEIGHT_CLASSES = "h-[358px]";

/** Height of `n` rows with the same row/gap sizes, for the `limit` variant
 *  (e.g. 3 rows = 210px on the dashboard), so an empty or short list keeps
 *  the same card height as a full one. */
const rowsHeight = (n: number) => n * 62 + (n - 1) * 12;

function getFileIcon(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  if (ext === "zip") return "lucide:archive";
  if (ext === "json") return "lucide:file-json";
  if (ext === "xml") return "lucide:file-code";
  if (ext === "csv") return "lucide:file-text";
  if (ext === "sql") return "lucide:database";
  if (ext === "tif" || ext === "tiff") return "lucide:file-image";
  if (["png", "jpg", "jpeg", "bmp"].includes(ext)) return "lucide:image";
  return "lucide:file-text";
}

function formatUploadDate(timestampMs: number): string {
  return new Date(timestampMs).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Every completed upload currently loaded, each a clickable card that opens
 *  straight into that upload's Document Panel entry. By default the 5 most
 *  recent show and the rest scroll (see SCROLL_HEIGHT_CLASSES); with `limit`
 *  only that many show, no scroll, plus an arrow link to the Document
 *  Panel. One shared component — used on both the Upload Records page and
 *  the Dashboard (end of Health Timeline) — so the two never drift apart and
 *  always redirect the same way. With no uploads yet it still shows its
 *  heading, with an empty-state message in place of the list. */
export function RecentUploadsCard({
  className,
  heading = "Recent Uploads",
  limit,
}: {
  className?: string;
  heading?: string;
  /** Show only this many uploads (no scroll) and an arrow link to the Document Panel. */
  limit?: number;
}) {
  const navigate = useNavigate();
  const { zipUploads, isLoading } = useUploadHistory();
  const recentSuccessfulUploads = zipUploads.filter((z) => z.status === "complete");

  if (isLoading) {
    return (
      <div className={cn("rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm", className)}>
        <h3 className="text-sm font-extrabold text-[#0A2947]">{heading}</h3>
        <div className="mt-4 flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl border border-[#E5E7EB] px-4 py-3">
              <Skeleton.Avatar active size={36} shape="circle" />
              <div className="min-w-0 flex-1">
                <Skeleton active title={{ width: "50%" }} paragraph={{ rows: 1, width: "30%" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (recentSuccessfulUploads.length === 0) {
    return (
      <div className={cn("rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm", className)}>
        <h3 className="text-sm font-extrabold text-[#0A2947]">{heading}</h3>
        <div className={cn("mt-4", !limit && SCROLL_HEIGHT_CLASSES)} style={limit ? { height: rowsHeight(limit) } : undefined}>
          <EmptyState
            className="h-full border-dashed py-0 shadow-none"
            icon={<Icon icon="lucide:file-text" className="h-10 w-10 !text-[#9CA3AF]" />}
            title="No medical records uploaded yet"
            description="Records you upload will appear here."
          />
        </div>
      </div>
    );
  }

  const shown = limit ? recentSuccessfulUploads.slice(0, limit) : recentSuccessfulUploads;
  // gap on a flex column, not space-y — immune to any margin-collapse from
  // elsewhere in the cascade, which is what was causing these cards to
  // visually crowd/overlap before.
  const rows = (
    <div className="flex flex-col gap-3 pr-1">
      {shown.map((entry) => (
        <button
          key={entry.id}
          type="button"
          onClick={() => navigate(`/patient/documents?uploadId=${entry.id}`)}
          className="flex w-full items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3 text-left transition-colors hover:border-[#F36F8F]/40 hover:bg-[#FFF9FA]"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F7F7F7]">
            <Icon icon={getFileIcon(entry.fileName)} className="h-4 w-4 !text-[#0A2947]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-[#0A2947]">{entry.title || entry.fileName}</p>
            <p className="text-xs text-[#9CA3AF]">{formatUploadDate(entry.uploadedAt)}</p>
          </div>
          <Icon icon="lucide:chevron-right" className="h-4 w-4 shrink-0 !text-[#9CA3AF]" />
        </button>
      ))}
    </div>
  );

  return (
    <div className={cn("rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm", className)}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-extrabold text-[#0A2947]">{heading}</h3>
        {limit && (
          <Button
            variant="link"
            aria-label="See all uploads in the Document Panel"
            title="See all uploads"
            onClick={() => navigate("/patient/documents")}
            className="!flex !h-6 !w-6 !items-center !justify-center !p-0 !text-[#9CA3AF] hover:!text-[#0A2947]"
          >
            <Icon icon="lucide:arrow-up-right" className="h-4 w-4" />
          </Button>
        )}
      </div>
      {limit ? (
        <div className="mt-4" style={{ minHeight: rowsHeight(limit) }}>{rows}</div>
      ) : (
        <InfiniteScrollContainer className={cn("mt-4", SCROLL_HEIGHT_CLASSES)}>{rows}</InfiniteScrollContainer>
      )}
    </div>
  );
}
